import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import VideoCard from '@/components/feature/VideoCard';
import { categories, featuredVideos, latestUpdates } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSort, setActiveSort] = useState('latest');

  const currentCategory = categories.find((c) => c.slug === slug);
  const allVideos = [...featuredVideos, ...latestUpdates];
  const categoryVideos = allVideos.filter((v) => v.categorySlug === slug);

  const sortedVideos = [...categoryVideos].sort((a, b) => {
    if (activeSort === 'rating') return b.rating - a.rating;
    if (activeSort === 'popular') return parseFloat(b.views.replace('万', '')) - parseFloat(a.views.replace('万', ''));
    return 0;
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!currentCategory) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 block mb-4"></i>
            <h2 className="text-xl font-heading font-bold text-foreground-800">分类未找到</h2>
            <p className="text-sm text-foreground-500 mt-2 mb-4">请返回首页选择其他分类</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 text-white text-sm cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const sortOptions = [
    { key: 'latest', label: '最新' },
    { key: 'popular', label: '最热' },
    { key: 'rating', label: '评分' },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-8">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory.name },
                ]}
              />
              <div className="flex items-center gap-4 mt-2">
                <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className={`${currentCategory.icon} text-2xl text-primary-500`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">{currentCategory.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="sticky top-16 z-30 bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-1 overflow-x-auto py-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-all whitespace-nowrap cursor-pointer ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Sort Controls */}
        <div className="w-full px-4 md:px-6 py-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              <p className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-900">{categoryVideos.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setActiveSort(opt.key)}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      activeSort === opt.key
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Video Grid */}
        <section className="w-full px-4 md:px-6 pb-14">
          <div className="max-w-7xl mx-auto">
            {sortedVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {sortedVideos.map((video) => (
                  <VideoCard
                    key={`${video.categorySlug}-${video.id}`}
                    id={video.id}
                    title={video.title}
                    category={video.category}
                    categorySlug={video.categorySlug}
                    thumbnail={video.thumbnail}
                    rating={video.rating}
                    views={video.views}
                    quality={video.quality}
                    duration={'duration' in video ? video.duration : undefined}
                    updateInfo={'updateInfo' in video ? video.updateInfo : undefined}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300 block mb-4"></i>
                <p className="text-foreground-500">该分类暂无视频，请查看其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}